import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { ShareDialog } from "../../components/ShareDialog";
import { Plus, Lock, FileDown, Pencil, Trash2, Search, Share2, ShieldCheck, Ban, CalendarClock } from "lucide-react";
import { toast } from "sonner";

// Formate une valeur "date_rdv" (input datetime-local, ex. "2026-08-26T14:30")
// en affichage court FR ("26/08/2026 14:30"). Renvoie null si absente/invalide,
// pour que l'appelant puisse afficher un tiret plutôt qu'une date fausse.
function formatRdv(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()} ${hours}:${minutes}`;
}

export default function InterventionListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [rdvFilter, setRdvFilter] = useState("all"); // "all" | "with" | "without"
  const [shareItem, setShareItem] = useState(null);

  const load = () => api.get("/interventions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    if (q && !((it.numero || "").toLowerCase().includes(q) || (it.client_nom || "").toLowerCase().includes(q) || (it.vendeur_nom || "").toLowerCase().includes(q))) return false;
    if (rdvFilter === "with" && !it.date_rdv) return false;
    if (rdvFilter === "without" && it.date_rdv) return false;
    return true;
  });

  const remove = async (id) => {
    try {
      await api.delete(`/interventions/${id}`);
      toast.success("Intervention supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Interventions">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="intervention-search-input" placeholder="Rechercher par n°, client, vendeur..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={rdvFilter} onValueChange={setRdvFilter}>
            <SelectTrigger data-testid="intervention-rdv-filter" className="w-full sm:w-[190px] shrink-0">
              <SelectValue placeholder="Rendez-vous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rendez-vous</SelectItem>
              <SelectItem value="with">Avec rendez-vous</SelectItem>
              <SelectItem value="without">Sans rendez-vous</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasPerm(user, "intervention", "create") && (
          <Link to="/interventions/new">
            <Button data-testid="intervention-add-button" className="gap-2 shrink-0 w-full sm:w-auto"><Plus size={16} /> Nouvelle intervention</Button>
          </Link>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="intervention-list">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3">N°</th><th className="p-3">Date</th><th className="p-3">Client</th><th className="p-3">Vendeur</th><th className="p-3">Rendez-vous</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const rdv = formatRdv(it.date_rdv);
              return (
              <tr key={it.id} className="border-t border-border" data-testid={`intervention-row-${it.id}`}>
                <td className="p-3">
                  {it.numero}
                  {it.is_shared_to_me && (
                    <span className="block text-xs text-muted-foreground" data-testid={`intervention-shared-badge-${it.id}`}>
                      Partagé par {it.shared_by_label} ({it.share_mode === "write" ? "écriture" : "lecture"})
                    </span>
                  )}
                  {it.annule ? (
                    <span className="flex items-center gap-1 text-xs text-destructive" data-testid={`intervention-status-annule-${it.id}`}><Ban size={12} />Annulé</span>
                  ) : it.status === "cloture" ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-600" data-testid={`intervention-status-cloture-${it.id}`}><ShieldCheck size={12} />Clôturé</span>
                  ) : null}
                </td>
                <td className="p-3">{it.date}</td>
                <td className="p-3">{it.client_nom}</td>
                <td className="p-3">{it.vendeur_nom}</td>
                <td className="p-3" data-testid={`intervention-rdv-${it.id}`}>
                  {rdv ? (
                    <span className="flex items-center gap-1.5 text-sky-700 dark:text-sky-400 font-medium">
                      <CalendarClock size={13} /> {rdv}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="p-3 text-right">
                  {it.can_open ? (
                    <div className="flex justify-end items-center gap-3">
                      <Link to={`/interventions/${it.id}`} data-testid={`intervention-open-${it.id}`} className="text-primary text-sm">
                        {hasPerm(user, "intervention", "edit") ? "Modifier" : "Voir"}
                      </Link>
                      <a href={`${api.defaults.baseURL}/interventions/${it.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`intervention-pdf-${it.id}`} title="PDF"><FileDown size={15} /></a>
                      {hasPerm(user, "partage_document") && !it.is_shared_to_me && (
                        <button data-testid={`intervention-share-${it.id}`} onClick={() => setShareItem(it.id)} title="Partager"><Share2 size={15} /></button>
                      )}
                      {hasPerm(user, "intervention", "delete") && !it.is_shared_to_me && it.status !== "cloture" && (
                        <button data-testid={`intervention-delete-${it.id}`} onClick={() => remove(it.id)} title="Supprimer" className="text-destructive"><Trash2 size={15} /></button>
                      )}
                    </div>
                  ) : (
                    <Lock size={14} className="text-muted-foreground inline" data-testid={`intervention-locked-${it.id}`} />
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ShareDialog open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)} module="interventions" itemId={shareItem} onShared={load} />
    </Layout>
  );
}
